export function formatMoney(cents: number | null | undefined, currency = "USD", opts: { cents?: boolean } = {}): string {
  if (cents == null) return "—";
  const showCents = opts.cents ?? cents % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(cents / 100);
}

/** "$3,800", "3800", "3,800.50" → cents. Returns null for anything that isn't a plain amount. */
export function parseMoney(input: string): number | null {
  const s = input.replace(/[\s$,]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

export interface CostSettings {
  taxBps: number;
  defaultPremiumBps: number;
  feesCentsBySource: Record<string, number>;
  premiumBpsBySource: Record<string, number>;
  transportCentsPerMile: number;
  home: { lat: number; lng: number } | null;
}

export const DEFAULT_COST_SETTINGS: CostSettings = {
  taxBps: 0,
  defaultPremiumBps: 1500,
  feesCentsBySource: {},
  premiumBpsBySource: {},
  transportCentsPerMile: 150,
  home: null,
};

export function milesBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export interface AllInBreakdown {
  bidCents: number;
  premiumBps: number;
  premiumCents: number;
  taxCents: number;
  feesCents: number;
  transportCents: number | null;
  miles: number | null;
  totalCents: number;
  premiumKnown: boolean;
}

/** What winning at `bidCents` would actually cost. Premium and tax compound the way auction invoices do. */
export function allInCost(
  bidCents: number,
  lot: { source: string; buyerPremiumBps: number | null; lat: number | null; lng: number | null; isVehicle: boolean },
  s: CostSettings,
): AllInBreakdown {
  const premiumKnown = lot.buyerPremiumBps != null || lot.source in s.premiumBpsBySource;
  const premiumBps = lot.buyerPremiumBps ?? s.premiumBpsBySource[lot.source] ?? s.defaultPremiumBps;
  const premiumCents = Math.round((bidCents * premiumBps) / 10_000);
  const taxCents = Math.round(((bidCents + premiumCents) * s.taxBps) / 10_000);
  const feesCents = s.feesCentsBySource[lot.source] ?? 0;
  let miles: number | null = null;
  let transportCents: number | null = null;
  if (lot.isVehicle && s.home && lot.lat != null && lot.lng != null) {
    miles = Math.round(milesBetween(s.home, { lat: lot.lat, lng: lot.lng }));
    transportCents = Math.round(miles * s.transportCentsPerMile);
  }
  return {
    bidCents, premiumBps, premiumCents, taxCents, feesCents, transportCents, miles, premiumKnown,
    totalCents: bidCents + premiumCents + taxCents + feesCents + (transportCents ?? 0),
  };
}
